import React, { useMemo, useState } from 'react';
import { ProductViewModel } from '~/lib/data-contract';
import { Link, Text } from '~/shared/components';
import {
    StyledColorVariant,
    StyledColorVariantList,
    StyledContent,
    StyledMoreButton,
} from './styled';
import { useTranslation } from '~/shared/utils/translation';
import { queries } from '~/theme/breakpoints';
import { ProductImage } from '~/shared/components/ProductImage';

export type Props = Pick<ProductViewModel, 'colors'> & { activeColorId?: string | null };

const MAX_ITEMS = 9;

export const ColorSelector = ({ colors, activeColorId }: Props) => {
    const [max, setMax] = useState(MAX_ITEMS);
    const { translate } = useTranslation();

    const activeColor = useMemo(
        () => colors?.find((color) => color.productColorId === activeColorId)?.color,
        [activeColorId, colors]
    );
    const remaining = useMemo(() => (colors || []).length - max, [colors, max]);

    return (
        <StyledContent>
            <Text as="div" variant="overline">
                {activeColor && translate('productDetails.activeColorVariant', { activeColor })}
            </Text>
            <StyledColorVariantList>
                {colors && colors?.length > 1
                    ? colors
                          ?.slice(0, max)
                          .map(({ image, productColorId, altText, color, url }) => (
                              <StyledColorVariant
                                  active={productColorId === activeColorId}
                                  key={url}
                              >
                                  <Link
                                      href={url || ''}
                                      scroll={false}
                                      prefetch={false}
                                      replace
                                      title={color || ''}
                                      type="router"
                                  >
                                      <ProductImage
                                          sizes={`${queries.md} 80px, 48px`}
                                          image={image}
                                          alt={altText || ''}
                                          quality={100}
                                          style={{
                                              width: '100%',
                                              height: '100%',
                                              objectFit: image?.url ? 'contain' : 'cover',
                                          }}
                                      />
                                  </Link>
                              </StyledColorVariant>
                          ))
                    : null}
                {remaining > 0 && (
                    <StyledMoreButton onClick={() => setMax(Infinity)} children={`+${remaining}`} />
                )}
            </StyledColorVariantList>
        </StyledContent>
    );
};
